/**
 * Business Rules Engine Service
 * TASK-008: Implements configurable IF-THEN rules per CONTEXT.md Section 2.2
 *
 * Features:
 * - Configurable business rules with JSON conditions
 * - Rule evaluation against data context
 * - Action execution (alerts, flags, tasks)
 * - Rule templates and presets
 * - Execution audit trail
 */

import { supabase } from '../supabase';
import { logErrorWithTrace } from '../api';
import type {
    BusinessRule,
    RuleExecution,
    RuleTemplate,
    RuleStatistics,
    RuleCategory,
    RuleTriggerEvent,
    RuleActionType,
    RuleSeverity,
    RuleCooldownScope,
    RuleCondition,
    RuleConditionGroup,
    RuleCreateInput,
    DateRange,
    PaginatedResult,
} from '../types';

// ============================================
// TYPES
// ============================================

export interface RulePaginationParams {
    page?: number;
    pageSize?: number;
    sortBy?: 'name' | 'priority' | 'execution_count' | 'last_executed_at' | 'created_at';
    sortOrder?: 'asc' | 'desc';
}

export interface RuleFilterParams {
    category?: RuleCategory | RuleCategory[];
    triggerEvent?: RuleTriggerEvent | RuleTriggerEvent[];
    isActive?: boolean;
    search?: string;
}

export interface RuleContext {
    [key: string]: unknown;
    // Common context fields
    po?: {
        id: string;
        total_units_ordered: number;
        total_units_received: number;
        delay_hours: number;
        is_late: boolean;
        short_ship_percentage: number;
        [key: string]: unknown;
    };
    supplier?: {
        id: string;
        reliability_grade: string;
        risk_score: number;
        [key: string]: unknown;
    };
    employee?: {
        id: string;
        void_count_shift: number;
        return_count_shift: number;
        [key: string]: unknown;
    };
    transaction?: {
        id: string;
        type: string;
        has_manager_auth: boolean;
        amount: number;
        [key: string]: unknown;
    };
    product?: {
        id: string;
        abc_class: string;
        shrinkage_rate: number;
        [key: string]: unknown;
    };
    inventory?: {
        current_qty: number;
        [key: string]: unknown;
    };
    store?: {
        id: string;
        inventory_accuracy: number;
        [key: string]: unknown;
    };
}

export interface RuleEvaluationResult {
    ruleId: string;
    ruleName: string;
    conditionsMet: boolean;
    conditionResults: Record<string, boolean>;
    evaluatedAt: string;
}

export interface RuleExecutionResult {
    ruleId: string;
    ruleName: string;
    triggered: boolean;
    actionTaken?: string;
    actionResultId?: string;
    actionResultType?: string;
    success: boolean;
    errorMessage?: string;
    executionTimeMs: number;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Map database row to BusinessRule type
 */
function mapDbToRule(row: Record<string, unknown>): BusinessRule {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        name: row.name,
        code: row.code,
        description: row.description,
        category: row.category,
        priority: row.priority ?? 100,
        triggerEvent: row.trigger_event,
        conditionJson: row.condition_json || { operator: 'AND', conditions: [] },
        actionType: row.action_type,
        actionParams: row.action_params || {},
        severity: row.severity || 'warning',
        isActive: row.is_active ?? true,
        isSystemRule: row.is_system_rule ?? false,
        executionCount: row.execution_count ?? 0,
        lastExecutedAt: row.last_executed_at,
        lastTriggeredAt: row.last_triggered_at,
        cooldownMinutes: row.cooldown_minutes ?? 0,
        cooldownScope: row.cooldown_scope || 'global',
        version: row.version ?? 1,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        createdBy: row.created_by,
    };
}

/**
 * Map database row to RuleExecution type
 */
function mapDbToExecution(row: Record<string, unknown>): RuleExecution {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        ruleId: row.rule_id,
        triggeredAt: row.triggered_at,
        triggerEvent: row.trigger_event,
        triggerData: row.trigger_data || {},
        triggerEntityType: row.trigger_entity_type,
        triggerEntityId: row.trigger_entity_id,
        conditionsMet: row.conditions_met ?? false,
        conditionResults: row.condition_results,
        actionTaken: row.action_taken,
        actionResultId: row.action_result_id,
        actionResultType: row.action_result_type,
        success: row.success ?? false,
        errorMessage: row.error_message,
        executionTimeMs: row.execution_time_ms,
        createdAt: row.created_at,
    };
}

/**
 * Map database row to RuleTemplate type
 */
function mapDbToTemplate(row: Record<string, unknown>): RuleTemplate {
    return {
        id: row.id,
        name: row.name,
        description: row.description,
        category: row.category,
        triggerEvent: row.trigger_event,
        conditionJson: row.condition_json || { operator: 'AND', conditions: [] },
        actionType: row.action_type,
        actionParams: row.action_params || {},
        severity: row.severity || 'warning',
        isRecommended: row.is_recommended ?? false,
        createdAt: row.created_at,
    };
}

/**
 * Get nested field value from object using dot notation
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
    return path.split('.').reduce((current: unknown, key: string) => {
        return current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined;
    }, obj as unknown);
}

/**
 * Evaluate a simple expression (very basic implementation)
 */
function evaluateExpression(expr: string, context: RuleContext): unknown {
    // Simple expression evaluation for common patterns
    // e.g., "po.total_units_ordered * 0.98"

    // Replace field references with values
    const fieldPattern = /([a-zA-Z_][a-zA-Z0-9_.]*)/g;
    let evalExpr = expr;

    const matches = expr.match(fieldPattern);
    if (matches) {
        for (const match of matches) {
            // Skip if it looks like a number or keyword
            if (/^\d/.test(match) || ['true', 'false', 'null'].includes(match)) {
                continue;
            }

            const value = getNestedValue(context, match);
            if (value !== undefined) {
                // Replace the field reference with its value
                evalExpr = evalExpr.replace(match, String(value));
            }
        }
    }

    // Safely evaluate the expression (basic arithmetic only)
    try {
        // Only allow numbers, operators, and parentheses
        if (/^[\d\s+\-*/.()]+$/.test(evalExpr)) {
            // eslint-disable-next-line no-new-func
            return Function(`"use strict"; return (${evalExpr})`)();
        }
        return evalExpr;
    } catch {
        return expr;
    }
}

/**
 * Evaluate a single condition
 */
function evaluateCondition(condition: RuleCondition, context: RuleContext): boolean {
    const fieldValue = getNestedValue(context, condition.field);

    // Determine the comparison value
    let compareValue: unknown;
    if (condition.valueExpr) {
        compareValue = evaluateExpression(condition.valueExpr, context);
    } else if (condition.fieldRef) {
        compareValue = getNestedValue(context, condition.fieldRef);
    } else {
        compareValue = condition.value;
    }

    // Apply operator
    switch (condition.operator) {
        case '==':
            return fieldValue === compareValue;
        case '!=':
            return fieldValue !== compareValue;
        case '>':
            return Number(fieldValue) > Number(compareValue);
        case '<':
            return Number(fieldValue) < Number(compareValue);
        case '>=':
            return Number(fieldValue) >= Number(compareValue);
        case '<=':
            return Number(fieldValue) <= Number(compareValue);
        case 'in':
            return Array.isArray(compareValue) && compareValue.includes(fieldValue);
        case 'not_in':
            return Array.isArray(compareValue) && !compareValue.includes(fieldValue);
        case 'contains':
            return typeof fieldValue === 'string' && fieldValue.includes(String(compareValue));
        case 'is_null':
            return fieldValue === null || fieldValue === undefined;
        case 'is_not_null':
            return fieldValue !== null && fieldValue !== undefined;
        default:
            return false;
    }
}

/**
 * Evaluate a condition group (AND/OR)
 */
function evaluateConditionGroup(
    group: RuleConditionGroup,
    context: RuleContext
): { result: boolean; details: Record<string, boolean> } {
    const details: Record<string, boolean> = {};

    const results = group.conditions.map((cond, index) => {
        if ('operator' in cond && 'conditions' in cond) {
            // Nested group
            const nestedResult = evaluateConditionGroup(cond as RuleConditionGroup, context);
            details[`group_${index}`] = nestedResult.result;
            Object.assign(details, nestedResult.details);
            return nestedResult.result;
        } else {
            // Single condition
            const condResult = evaluateCondition(cond as RuleCondition, context);
            details[(cond as RuleCondition).field] = condResult;
            return condResult;
        }
    });

    const result = group.operator === 'AND'
        ? results.every(r => r)
        : results.some(r => r);

    return { result, details };
}

// ============================================
// RULES ENGINE SERVICE
// ============================================

export const rulesEngineService = {
    // ============================================
    // RULE MANAGEMENT
    // ============================================

    /**
     * Get paginated list of rules
     */
    async getRules(
        tenantId: string,
        pagination?: RulePaginationParams,
        filters?: RuleFilterParams
    ): Promise<PaginatedResult<BusinessRule>> {
        try {
            const page = pagination?.page ?? 1;
            const pageSize = pagination?.pageSize ?? 20;
            const sortBy = pagination?.sortBy ?? 'priority';
            const sortOrder = pagination?.sortOrder ?? 'asc';
            const offset = (page - 1) * pageSize;

            let query = supabase
                .from('business_rules')
                .select('*', { count: 'exact' })
                .eq('tenant_id', tenantId);

            // Apply filters
            if (filters?.category) {
                const categories = Array.isArray(filters.category) ? filters.category : [filters.category];
                query = query.in('category', categories);
            }

            if (filters?.triggerEvent) {
                const events = Array.isArray(filters.triggerEvent) ? filters.triggerEvent : [filters.triggerEvent];
                query = query.in('trigger_event', events);
            }

            if (filters?.isActive !== undefined) {
                query = query.eq('is_active', filters.isActive);
            }

            if (filters?.search) {
                query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
            }

            // Apply sorting and pagination
            query = query
                .order(sortBy, { ascending: sortOrder === 'asc' })
                .range(offset, offset + pageSize - 1);

            const { data, error, count } = await query;

            if (error) throw error;

            return {
                data: (data || []).map(mapDbToRule),
                total: count ?? 0,
                page,
                pageSize,
                totalPages: Math.ceil((count ?? 0) / pageSize),
            };
        } catch (error) {
            logErrorWithTrace('rulesEngineService.getRules', error);
            throw error;
        }
    },

    /**
     * Get active rules for a specific trigger event
     */
    async getActiveRulesForEvent(
        tenantId: string,
        triggerEvent: RuleTriggerEvent
    ): Promise<BusinessRule[]> {
        try {
            const { data, error } = await supabase
                .from('business_rules')
                .select('*')
                .eq('tenant_id', tenantId)
                .eq('trigger_event', triggerEvent)
                .eq('is_active', true)
                .order('priority', { ascending: true });

            if (error) throw error;

            return (data || []).map(mapDbToRule);
        } catch (error) {
            logErrorWithTrace('rulesEngineService.getActiveRulesForEvent', error);
            throw error;
        }
    },

    /**
     * Get a single rule by ID
     */
    async getRule(id: string): Promise<BusinessRule | null> {
        try {
            const { data, error } = await supabase
                .from('business_rules')
                .select('*')
                .eq('id', id)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }

            return data ? mapDbToRule(data) : null;
        } catch (error) {
            logErrorWithTrace('rulesEngineService.getRule', error);
            throw error;
        }
    },

    /**
     * Create a new rule
     */
    async createRule(input: RuleCreateInput, tenantId: string): Promise<BusinessRule> {
        try {
            const { data, error } = await supabase
                .from('business_rules')
                .insert({
                    tenant_id: tenantId,
                    name: input.name,
                    code: input.code,
                    description: input.description,
                    category: input.category,
                    priority: input.priority ?? 100,
                    trigger_event: input.triggerEvent,
                    condition_json: input.conditionJson,
                    action_type: input.actionType,
                    action_params: input.actionParams || {},
                    severity: input.severity ?? 'warning',
                    cooldown_minutes: input.cooldownMinutes ?? 0,
                    cooldown_scope: input.cooldownScope ?? 'global',
                    is_active: input.isActive ?? true,
                })
                .select()
                .single();

            if (error) throw error;

            return mapDbToRule(data);
        } catch (error) {
            logErrorWithTrace('rulesEngineService.createRule', error);
            throw error;
        }
    },

    /**
     * Update a rule
     */
    async updateRule(
        id: string,
        updates: Partial<RuleCreateInput>
    ): Promise<BusinessRule> {
        try {
            const updateData: Record<string, unknown> = {};

            if (updates.name !== undefined) updateData.name = updates.name;
            if (updates.code !== undefined) updateData.code = updates.code;
            if (updates.description !== undefined) updateData.description = updates.description;
            if (updates.priority !== undefined) updateData.priority = updates.priority;
            if (updates.conditionJson !== undefined) updateData.condition_json = updates.conditionJson;
            if (updates.actionType !== undefined) updateData.action_type = updates.actionType;
            if (updates.actionParams !== undefined) updateData.action_params = updates.actionParams;
            if (updates.severity !== undefined) updateData.severity = updates.severity;
            if (updates.cooldownMinutes !== undefined) updateData.cooldown_minutes = updates.cooldownMinutes;
            if (updates.cooldownScope !== undefined) updateData.cooldown_scope = updates.cooldownScope;
            if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

            const { data, error } = await supabase
                .from('business_rules')
                .update(updateData)
                .eq('id', id)
                .select()
                .single();

            if (error) throw error;

            return mapDbToRule(data);
        } catch (error) {
            logErrorWithTrace('rulesEngineService.updateRule', error);
            throw error;
        }
    },

    /**
     * Toggle rule active state
     */
    async toggleRule(id: string, isActive: boolean): Promise<BusinessRule> {
        return this.updateRule(id, { isActive });
    },

    /**
     * Delete a rule (only non-system rules)
     */
    async deleteRule(id: string): Promise<boolean> {
        try {
            // Check if it's a system rule
            const { data: rule } = await supabase
                .from('business_rules')
                .select('is_system_rule')
                .eq('id', id)
                .single();

            if (rule?.is_system_rule) {
                throw new Error('Cannot delete system rules');
            }

            const { error } = await supabase
                .from('business_rules')
                .delete()
                .eq('id', id);

            if (error) throw error;

            return true;
        } catch (error) {
            logErrorWithTrace('rulesEngineService.deleteRule', error);
            throw error;
        }
    },

    // ============================================
    // RULE EVALUATION & EXECUTION
    // ============================================

    /**
     * Evaluate a single rule against context
     */
    evaluateRule(rule: BusinessRule, context: RuleContext): RuleEvaluationResult {
        const evaluation = evaluateConditionGroup(rule.conditionJson, context);

        return {
            ruleId: rule.id,
            ruleName: rule.name,
            conditionsMet: evaluation.result,
            conditionResults: evaluation.details,
            evaluatedAt: new Date().toISOString(),
        };
    },

    /**
     * Execute rules for an event
     */
    async executeRulesForEvent(
        tenantId: string,
        triggerEvent: RuleTriggerEvent,
        context: RuleContext,
        entityType?: string,
        entityId?: string
    ): Promise<RuleExecutionResult[]> {
        const results: RuleExecutionResult[] = [];
        const startTime = performance.now();

        try {
            // Get active rules for this event
            const rules = await this.getActiveRulesForEvent(tenantId, triggerEvent);

            for (const rule of rules) {
                const ruleStartTime = performance.now();

                try {
                    // Check cooldown
                    if (rule.cooldownMinutes > 0) {
                        const canExecute = await this.checkCooldown(
                            rule.id,
                            rule.cooldownScope,
                            entityId
                        );
                        if (!canExecute) {
                            continue; // Skip this rule due to cooldown
                        }
                    }

                    // Evaluate rule
                    const evaluation = this.evaluateRule(rule, context);

                    // Record execution
                    const executionTimeMs = Math.round(performance.now() - ruleStartTime);

                    let actionTaken: string | undefined;
                    let actionResultId: string | undefined;
                    let actionResultType: string | undefined;

                    // Execute action if conditions met
                    if (evaluation.conditionsMet) {
                        const actionResult = await this.executeAction(
                            rule,
                            context,
                            tenantId
                        );
                        actionTaken = actionResult.action;
                        actionResultId = actionResult.resultId;
                        actionResultType = actionResult.resultType;

                        // Update rule last triggered time
                        await supabase
                            .from('business_rules')
                            .update({
                                last_triggered_at: new Date().toISOString(),
                                execution_count: rule.executionCount + 1,
                            })
                            .eq('id', rule.id);
                    }

                    // Update last executed time
                    await supabase
                        .from('business_rules')
                        .update({ last_executed_at: new Date().toISOString() })
                        .eq('id', rule.id);

                    // Log execution
                    await this.logExecution({
                        tenantId,
                        ruleId: rule.id,
                        triggerEvent,
                        triggerData: context,
                        triggerEntityType: entityType,
                        triggerEntityId: entityId,
                        conditionsMet: evaluation.conditionsMet,
                        conditionResults: evaluation.conditionResults,
                        actionTaken,
                        actionResultId,
                        actionResultType,
                        success: true,
                        executionTimeMs,
                    });

                    results.push({
                        ruleId: rule.id,
                        ruleName: rule.name,
                        triggered: evaluation.conditionsMet,
                        actionTaken,
                        actionResultId,
                        actionResultType,
                        success: true,
                        executionTimeMs,
                    });
                } catch (ruleError) {
                    const executionTimeMs = Math.round(performance.now() - ruleStartTime);
                    const errorMessage = ruleError instanceof Error ? ruleError.message : String(ruleError);

                    // Log failed execution
                    await this.logExecution({
                        tenantId,
                        ruleId: rule.id,
                        triggerEvent,
                        triggerData: context,
                        triggerEntityType: entityType,
                        triggerEntityId: entityId,
                        conditionsMet: false,
                        success: false,
                        errorMessage,
                        executionTimeMs,
                    });

                    results.push({
                        ruleId: rule.id,
                        ruleName: rule.name,
                        triggered: false,
                        success: false,
                        errorMessage,
                        executionTimeMs,
                    });
                }
            }

            return results;
        } catch (error) {
            logErrorWithTrace('rulesEngineService.executeRulesForEvent', error);
            throw error;
        }
    },

    /**
     * Check if rule is in cooldown
     */
    async checkCooldown(
        ruleId: string,
        cooldownScope: RuleCooldownScope,
        entityId?: string
    ): Promise<boolean> {
        if (cooldownScope === 'none') return true;

        try {
            const { data: rule } = await supabase
                .from('business_rules')
                .select('last_triggered_at, cooldown_minutes')
                .eq('id', ruleId)
                .single();

            if (!rule?.last_triggered_at || !rule?.cooldown_minutes) return true;

            const lastTriggered = new Date(rule.last_triggered_at);
            const cooldownEnd = new Date(lastTriggered.getTime() + rule.cooldown_minutes * 60000);

            if (cooldownScope === 'global') {
                return new Date() >= cooldownEnd;
            }

            if (cooldownScope === 'per_entity' && entityId) {
                // Check last execution for this specific entity
                const { data: execution } = await supabase
                    .from('rule_executions')
                    .select('triggered_at')
                    .eq('rule_id', ruleId)
                    .eq('trigger_entity_id', entityId)
                    .eq('conditions_met', true)
                    .order('triggered_at', { ascending: false })
                    .limit(1)
                    .single();

                if (!execution) return true;

                const entityLastTriggered = new Date(execution.triggered_at);
                const entityCooldownEnd = new Date(
                    entityLastTriggered.getTime() + rule.cooldown_minutes * 60000
                );

                return new Date() >= entityCooldownEnd;
            }

            return true;
        } catch {
            return true; // Allow execution on error
        }
    },

    /**
     * Execute a rule action
     */
    async executeAction(
        rule: BusinessRule,
        context: RuleContext,
        tenantId: string
    ): Promise<{ action: string; resultId?: string; resultType?: string }> {
        const params = rule.actionParams;

        switch (rule.actionType) {
            case 'create_alert': {
                const { data: alert } = await supabase
                    .from('alerts')
                    .insert({
                        tenant_id: tenantId,
                        store_id: context.store?.id || context.po?.store_id,
                        type: params.type || rule.name,
                        description: params.description || `Rule triggered: ${rule.name}`,
                        category: params.category || 'Operativo',
                        potential_impact: rule.severity === 'critical' ? 'Alto' : rule.severity === 'warning' ? 'Medio' : 'Bajo',
                        status: 'new',
                        metadata: { rule_id: rule.id, trigger_context: context },
                    })
                    .select('id')
                    .single();

                return {
                    action: 'create_alert',
                    resultId: alert?.id,
                    resultType: 'alert',
                };
            }

            case 'create_task': {
                const { data: task } = await supabase
                    .from('tasks')
                    .insert({
                        tenant_id: tenantId,
                        store_id: context.store?.id,
                        title: params.title || rule.name,
                        description: params.description || `Task created by rule: ${rule.name}`,
                        priority: params.priority || 'medium',
                        status: 'pending',
                    })
                    .select('id')
                    .single();

                return {
                    action: 'create_task',
                    resultId: task?.id,
                    resultType: 'task',
                };
            }

            case 'flag_entity': {
                // Update the entity with a flag
                if (context.po?.id && params.flag) {
                    await supabase
                        .from('purchase_orders')
                        .update({
                            requires_review: true,
                            review_reason: params.flag,
                        })
                        .eq('id', context.po.id);
                }

                if (context.supplier?.id && params.update_supplier_metrics) {
                    // Supplier flagging is handled by triggers
                }

                return {
                    action: `flag_entity:${params.flag}`,
                };
            }

            case 'update_status': {
                if (context.po?.id && params.status) {
                    await supabase
                        .from('purchase_orders')
                        .update({ status: params.status })
                        .eq('id', context.po.id);
                }

                return {
                    action: `update_status:${params.status}`,
                };
            }

            case 'notify': {
                // Notification would integrate with notification service
                // For now, just log
                return {
                    action: `notify:${params.recipients || 'default'}`,
                };
            }

            case 'log_only':
            default:
                return {
                    action: 'log_only',
                };
        }
    },

    /**
     * Log rule execution
     */
    async logExecution(execution: {
        tenantId: string;
        ruleId: string;
        triggerEvent: RuleTriggerEvent;
        triggerData: RuleContext;
        triggerEntityType?: string;
        triggerEntityId?: string;
        conditionsMet: boolean;
        conditionResults?: Record<string, boolean>;
        actionTaken?: string;
        actionResultId?: string;
        actionResultType?: string;
        success: boolean;
        errorMessage?: string;
        executionTimeMs: number;
    }): Promise<void> {
        try {
            await supabase
                .from('rule_executions')
                .insert({
                    tenant_id: execution.tenantId,
                    rule_id: execution.ruleId,
                    trigger_event: execution.triggerEvent,
                    trigger_data: execution.triggerData,
                    trigger_entity_type: execution.triggerEntityType,
                    trigger_entity_id: execution.triggerEntityId,
                    conditions_met: execution.conditionsMet,
                    condition_results: execution.conditionResults,
                    action_taken: execution.actionTaken,
                    action_result_id: execution.actionResultId,
                    action_result_type: execution.actionResultType,
                    success: execution.success,
                    error_message: execution.errorMessage,
                    execution_time_ms: execution.executionTimeMs,
                });
        } catch (error) {
            logErrorWithTrace('rulesEngineService.logExecution', error);
            // Don't throw - logging failure shouldn't break rule execution
        }
    },

    // ============================================
    // RULE TEMPLATES
    // ============================================

    /**
     * Get all rule templates
     */
    async getTemplates(): Promise<RuleTemplate[]> {
        try {
            const { data, error } = await supabase
                .from('rule_templates')
                .select('*')
                .order('is_recommended', { ascending: false })
                .order('category');

            if (error) throw error;

            return (data || []).map(mapDbToTemplate);
        } catch (error) {
            logErrorWithTrace('rulesEngineService.getTemplates', error);
            throw error;
        }
    },

    /**
     * Get recommended templates
     */
    async getRecommendedTemplates(): Promise<RuleTemplate[]> {
        try {
            const { data, error } = await supabase
                .from('rule_templates')
                .select('*')
                .eq('is_recommended', true)
                .order('category');

            if (error) throw error;

            return (data || []).map(mapDbToTemplate);
        } catch (error) {
            logErrorWithTrace('rulesEngineService.getRecommendedTemplates', error);
            throw error;
        }
    },

    /**
     * Create rule from template
     */
    async createRuleFromTemplate(
        templateId: string,
        tenantId: string,
        customizations?: Partial<RuleCreateInput>
    ): Promise<BusinessRule> {
        try {
            // Get template
            const { data: template, error: templateError } = await supabase
                .from('rule_templates')
                .select('*')
                .eq('id', templateId)
                .single();

            if (templateError) throw templateError;
            if (!template) throw new Error('Template not found');

            // Create rule with template values + customizations
            const ruleInput: RuleCreateInput = {
                name: customizations?.name ?? template.name,
                description: customizations?.description ?? template.description,
                category: template.category,
                triggerEvent: template.trigger_event,
                conditionJson: customizations?.conditionJson ?? template.condition_json,
                actionType: template.action_type,
                actionParams: customizations?.actionParams ?? template.action_params,
                severity: customizations?.severity ?? template.severity,
                priority: customizations?.priority,
                cooldownMinutes: customizations?.cooldownMinutes,
                cooldownScope: customizations?.cooldownScope,
                isActive: customizations?.isActive ?? true,
            };

            return this.createRule(ruleInput, tenantId);
        } catch (error) {
            logErrorWithTrace('rulesEngineService.createRuleFromTemplate', error);
            throw error;
        }
    },

    // ============================================
    // STATISTICS & AUDIT
    // ============================================

    /**
     * Get rule execution history
     */
    async getExecutions(
        ruleId: string,
        limit: number = 50
    ): Promise<RuleExecution[]> {
        try {
            const { data, error } = await supabase
                .from('rule_executions')
                .select('*')
                .eq('rule_id', ruleId)
                .order('triggered_at', { ascending: false })
                .limit(limit);

            if (error) throw error;

            return (data || []).map(mapDbToExecution);
        } catch (error) {
            logErrorWithTrace('rulesEngineService.getExecutions', error);
            throw error;
        }
    },

    /**
     * Get rule statistics
     */
    async getRuleStatistics(tenantId: string): Promise<RuleStatistics[]> {
        try {
            const { data, error } = await supabase
                .from('rule_statistics')
                .select('*')
                .eq('tenant_id', tenantId);

            if (error) throw error;

            return (data || []).map(row => ({
                id: row.id,
                tenantId: row.tenant_id,
                name: row.name,
                category: row.category,
                triggerEvent: row.trigger_event,
                isActive: row.is_active ?? true,
                executionCount: row.execution_count ?? 0,
                lastExecutedAt: row.last_executed_at,
                lastTriggeredAt: row.last_triggered_at,
                executions30d: row.executions_30d ?? 0,
                triggers30d: row.triggers_30d ?? 0,
                failures30d: row.failures_30d ?? 0,
                avgExecutionTimeMs: row.avg_execution_time_ms,
            }));
        } catch (error) {
            logErrorWithTrace('rulesEngineService.getRuleStatistics', error);
            throw error;
        }
    },

    /**
     * Get rule execution stats for a date range
     */
    async getExecutionStats(
        tenantId: string,
        dateRange: DateRange
    ): Promise<{
        totalExecutions: number;
        totalTriggers: number;
        totalFailures: number;
        avgExecutionTimeMs: number;
        byCategory: Record<RuleCategory, { executions: number; triggers: number }>;
    }> {
        try {
            const { data, error } = await supabase
                .from('rule_executions')
                .select(`
                    conditions_met,
                    success,
                    execution_time_ms,
                    business_rules!inner(category)
                `)
                .eq('tenant_id', tenantId)
                .gte('triggered_at', dateRange.start)
                .lte('triggered_at', dateRange.end);

            if (error) throw error;

            const executions = data || [];
            const totalExecutions = executions.length;
            const totalTriggers = executions.filter(e => e.conditions_met).length;
            const totalFailures = executions.filter(e => !e.success).length;
            const avgExecutionTimeMs = totalExecutions > 0
                ? executions.reduce((sum, e) => sum + (e.execution_time_ms || 0), 0) / totalExecutions
                : 0;

            const byCategory: Record<string, { executions: number; triggers: number }> = {};

            executions.forEach(e => {
                const category = (e.business_rules as { category: string } | null)?.category || 'custom';
                if (!byCategory[category]) {
                    byCategory[category] = { executions: 0, triggers: 0 };
                }
                byCategory[category].executions++;
                if (e.conditions_met) {
                    byCategory[category].triggers++;
                }
            });

            return {
                totalExecutions,
                totalTriggers,
                totalFailures,
                avgExecutionTimeMs,
                byCategory: byCategory as Record<RuleCategory, { executions: number; triggers: number }>,
            };
        } catch (error) {
            logErrorWithTrace('rulesEngineService.getExecutionStats', error);
            throw error;
        }
    },
};

export default rulesEngineService;
